// Directory.js — /businesses progressive-render directory.
//
// UPSTREAM backend it talks to:
//   - GET /api/businesses?paginated=true&limit=FETCH_PAGE_SIZE&offset=... &country_code / &region_slug
//     (see backend/routes/businesses.py :: get_businesses)
//
// DOWNSTREAM links this page fans out to:
//   - /businesses/:slug detail view
//   - /map/:iso2 (BusinessMapCountry.js) — shares the same visibility rules
//     (global-scope records must pass every filter).
//
// Invariants enforced here:
//   - `isGlobalScope` must stay symmetric with backend `global_or` in
//     routes/businesses.py. Adding a new global signal to one side without
//     the other causes region-filter gaps (see Bitrefill-in-Africa bug).
//   - AU is NOT a default country. Inferred country only fills the region
//     via geolocation; the country selector defaults to "All".
//   - Refetch fires on region OR country change (commit 1955d6e).
import { useState, useEffect, useMemo, memo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, X, Globe, Phone, Mail, MapPin, Clock, Zap, ExternalLink, Copy, Navigation, ChevronDown, SlidersHorizontal, Flag } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import FavoriteButton from '@/components/FavoriteButton';
import { QRCodeButton } from '@/components/QRCodeModal';
import NewBadge from '@/components/NewBadge';
import NearbyButton from '@/components/NearbyButton';
import useInferredCountry from '@/hooks/useInferredCountry';
import regionsData from '@/data/regions.json';
import countriesData from '@/data/countries.json';
import categoriesData from '@/data/categories.json';
import COUNTRY_NAMES from '@/data/countryNames';
import { safeHref } from '../utils/url';
import { isGlobalScope } from '../utils/businessScope';

const API = '/api';
const FETCH_PAGE_SIZE = 100;


const ORANGELABEL_LOGO = "/logos/orangelabel-96.png";
const BTCMAP_LOGO = "https://btcmap.org/images/logo.svg";
const ABIB_LOGO = "/logos/abib-96.png";

// Views
const VIEW_ORANGELABEL = 'orangelabel';
const VIEW_BTCMAP = 'btcmap';


const REGION_LIST = Object.entries(regionsData).map(([key, val]) => ({
  key,
  name: val.name,
  countries: val.countries,
}));

const isATM = (b) => (b.category || []).some(c => c.toLowerCase().includes('atm')) || (b.business_name || '').toLowerCase().includes('atm');

const matchesCategory = (b, cat) => {
  if (cat === 'All') return true;
  if (cat === 'Bitcoin ATM') return isATM(b);
  const cats = b.category || [];
  const catLower = cat.toLowerCase();
  return cats.some(c => c.toLowerCase().includes(catLower));
};

const formatWebsiteUrl = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

// Build display address from components
const getDisplayAddress = (b) => {
  if (b.full_address) return b.full_address;

  const parts = [];
  if (b.addr_suburb) {
    parts.push(b.addr_suburb);
  } else if (b.addr_city) {
    parts.push(b.addr_city);
  }
  if (b.addr_state || b.based_state) {
    parts.push(b.addr_state || b.based_state);
  }
  if (b.addr_postcode) {
    parts.push(b.addr_postcode);
  }

  return parts.join(', ') || b.region || '';
};

// Loading skeleton component
const BusinessCardSkeleton = () => (
  <div className="bg-card rounded-xl border border-border p-4 animate-pulse">
    <div className="flex gap-3">
      <div className="flex-shrink-0">
        <div className="w-5 h-5 bg-muted rounded"></div>
      </div>
      <div className="flex-1 space-y-3">
        <div className="h-4 bg-muted rounded w-3/4"></div>
        <div className="space-y-2">
          <div className="h-3 bg-muted rounded w-full"></div>
          <div className="h-3 bg-muted rounded w-1/2"></div>
        </div>
        <div className="flex gap-1 justify-end">
          <div className="h-4 w-16 bg-muted rounded"></div>
        </div>
      </div>
    </div>
  </div>
);

// Enhanced business card component
const BusinessCard = memo(({ business: b }) => {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewReason, setReviewReason] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);

  const copyAddress = () => {
    const address = getDisplayAddress(b);
    navigator.clipboard.writeText(address);
    toast.success('Address copied!');
  };

  const submitReview = async () => {
    const reason = reviewReason.trim();
    if (!reason) {
      toast.error('Please describe the issue so an admin can action it.');
      return;
    }
    setReviewSubmitting(true);
    try {
      const res = await fetch(`${API}/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_id: b.id,
          business_name: b.business_name,
          kind: 'flag',
          reason,
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setReviewSent(true);
      toast.success('Reported — thanks, an admin will review.');
      setReviewOpen(false);
      setReviewReason('');
    } catch (err) {
      toast.error('Could not send report. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const openDirections = () => {
    const address = getDisplayAddress(b);
    const lat = b.latitude || b.osm_lat;
    const lng = b.longitude || b.osm_lon;

    let url;
    if (lat && lng) {
      url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    } else {
      url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Format distance
  const formatDistance = (km) => {
    if (km === null || km === undefined) return null;
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1)} km`;
  };

  const distanceDisplay = formatDistance(b.distance_km);
  const hasLocation = b.latitude && b.longitude;

  return (
    <div
      className={`group bg-card rounded-xl border border-border p-4 transition-all duration-200 hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5 relative ${
        b.abib_member && !b.btc_map ? 'bg-gradient-to-br from-orange-50/80 to-card dark:from-orange-950/20 dark:to-card border-orange-200 dark:border-orange-900' : ''
      }`}
      data-testid={`business-card-${b.id}`}
    >
      {/* Top Right Actions: Distance, New Badge, ABIB Logo */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        {distanceDisplay && hasLocation && (
          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
            {distanceDisplay}
          </span>
        )}
        {distanceDisplay && !hasLocation && (
          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full" title="Service-based business - no physical location">
            Service
          </span>
        )}
        <NewBadge createdAt={b.created_at} daysThreshold={30} />
        {b.abib_member && !b.btc_map && (
          <img
            src={ABIB_LOGO}
            alt="Australian Bitcoin Industry Body Member"
            title="Australian Bitcoin Industry Body Member"
            className="h-6 cursor-help opacity-80 group-hover:opacity-100 transition-opacity"
          />
        )}
      </div>

      <div className="flex gap-3">
        {/* Source Logo + Lightning */}
        <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
          <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
            {b.btc_map ? (
              <img src={BTCMAP_LOGO} alt="BTCMap" className="h-5 w-5 object-contain" />
            ) : (
              <img src={ORANGELABEL_LOGO} alt="OrangeLabel" className="h-5" />
            )}
          </div>
          {/* Lightning logo under source logo */}
          {b.payment_lightning && (
            <div className="flex items-center gap-0.5" title="Accepts Lightning">
              <Zap className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="font-semibold text-foreground truncate pr-16 group-hover:text-primary transition-colors">{b.business_name}</h3>

            <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
              <p className="flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted-foreground/70" />
                <span className="line-clamp-2">{getDisplayAddress(b)}</span>
              </p>

              {b.opening_hours && (
                <p className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground/70" />
                  <span className="truncate">{b.opening_hours}</span>
                </p>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-2 border-t border-border/50">
            {safeHref(b.website) && (
              <a
                href={safeHref(b.website)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 hover:bg-primary/20 rounded-md text-xs font-medium text-primary transition-colors"
              >
                <Globe className="w-3 h-3" /> Web
              </a>
            )}

            {b.contact && !b.contact.includes('@') && (
              <a
                href={`tel:${b.contact.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 rounded-md text-xs font-medium text-green-700 dark:text-green-400 transition-colors"
              >
                <Phone className="w-3 h-3" /> Call
              </a>
            )}

            {b.contact && b.contact.includes('@') && (
              <a
                href={`mailto:${b.contact}`}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-md text-xs font-medium text-blue-700 dark:text-blue-400 transition-colors"
              >
                <Mail className="w-3 h-3" /> Email
              </a>
            )}

            <button
              onClick={copyAddress}
              className="inline-flex items-center gap-1 px-2 py-1 bg-muted hover:bg-muted/80 rounded-md text-xs font-medium text-muted-foreground transition-colors"
              title="Copy address"
            >
              <Copy className="w-3 h-3" />
            </button>

            <button
              onClick={openDirections}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500 hover:bg-blue-600 rounded-md text-xs font-medium text-white transition-colors"
              title="Get directions"
            >
              <Navigation className="w-3 h-3" />
            </button>

            {/* QR Code Button */}
            <QRCodeButton business={{ id: b.id, name: b.business_name }} size="small" />

            {/* Favorite Button */}
            <FavoriteButton
              business={{ id: b.id, name: b.business_name, category: b.category?.[0], city: b.addr_city || b.region }}
              size="small"
            />

            {/* Report issue — OL listings only */}
            {!b.btc_map && (
              <button
                onClick={() => setReviewOpen(true)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  reviewSent
                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
                title={reviewSent ? 'Already reported' : 'Report an issue with this listing'}
                aria-label="Report issue"
              >
                <Flag className="w-3 h-3" />
              </button>
            )}

            {/* Category tags */}
            <div className="flex-1"></div>
            {(b.category || []).length > 0 && (
              <div className="flex flex-wrap gap-1 justify-end">
                {b.category.slice(0, 2).map((cat) => (
                  <span key={cat} className="text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded">
                    {cat}
                  </span>
                ))}
                {b.category.length > 2 && (
                  <span className="text-[10px] text-muted-foreground">+{b.category.length - 2}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report-issue dialog (flag with reason). Edit suggestions with
          structured field changes live on the map popup (/map/:iso2) —
          both funnel into /admin/businesses › Community Feedback. */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Report an issue with {b.business_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              An admin will review and action. For a structured correction
              (website, address, categories), use the map view — each pin has
              a <em>Suggest edit</em> button.
            </p>
            <div>
              <Label htmlFor={`review-reason-${b.id}`} className="text-xs font-medium">
                What's wrong? <span className="text-red-500">*</span>
              </Label>
              <textarea
                id={`review-reason-${b.id}`}
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value.slice(0, 500))}
                placeholder="e.g. closed down, no longer accepts Bitcoin, duplicate listing…"
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[72px]"
              />
              <p className="mt-1 text-[10px] text-muted-foreground text-right">
                {reviewReason.length}/500
              </p>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setReviewOpen(false)}
              disabled={reviewSubmitting}
              className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={submitReview}
              disabled={reviewSubmitting || !reviewReason.trim()}
              className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {reviewSubmitting ? 'Sending…' : 'Submit report'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

// Filter chip component
const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">
    {label}
    <button onClick={onRemove} className="hover:text-primary/70 transition-colors ml-0.5" aria-label={`Remove ${label} filter`}>
      <X className="w-3 h-3" />
    </button>
  </span>
);

// Primary view toggle card (slim, no collapsible sub-filter)
const ViewToggleCard = ({ view, activeView, onSelect, title, description, count, loading }) => {
  const isActive = activeView === view;

  return (
    <Card
      data-testid={`view-card-${view}`}
      onClick={() => onSelect(view)}
      className={`cursor-pointer transition-all duration-200 select-none ${
        isActive
          ? 'border-2 border-primary bg-primary/5 shadow-md'
          : 'border-2 border-border hover:border-primary/40 hover:shadow-sm'
      }`}
    >
      {/* Card header row */}
      <div className="flex items-center gap-3 p-3 sm:p-4">
        {/* Radio indicator */}
        <div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
          isActive ? 'border-primary bg-primary' : 'border-muted-foreground/40'
        }`}>
          {isActive && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
        </div>

        {/* Title + description */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-foreground">{title}</div>
          <div className="text-xs text-muted-foreground truncate">{description}</div>
        </div>

        {/* Count badge */}
        <div className="flex-shrink-0">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            isActive ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          }`}>
            {loading ? '…' : `${count} listings`}
          </span>
        </div>
      </div>
    </Card>
  );
};

// --------------------------------------------------------------------------
// Progressive paginated fetch hook
// --------------------------------------------------------------------------

/**
 * usePaginatedBusinesses
 *
 * Fetches businesses page-by-page, appending to the returned array after each
 * page. Callers get instant first render (skeletons → first 100 cards), then
 * see cards flow in as subsequent pages arrive.
 *
 * Returns:
 *   allBusinesses  — accumulated array (grows with each page)
 *   loadingFirst   — true while first page is in-flight (show skeletons)
 *   loadingMore    — true while subsequent pages are loading
 *   total          — server-reported total (or null until first page returns)
 *   loadedCount    — how many items are loaded so far
 *   resetAndFetch  — call with (params) to restart the streaming fetch
 */
function usePaginatedBusinesses(initialParams = {}) {
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [error, setError] = useState(false);

  // Use a ref to track the current fetch session; stale sessions abort silently.
  const sessionRef = useRef(0);
  const paramsRef = useRef(initialParams);

  const doFetch = useCallback(async (params, session) => {
    setAllBusinesses([]);
    setLoadedCount(0);
    setTotal(null);
    setLoadingFirst(true);
    setLoadingMore(false);
    setError(false);

    let offset = 0;
    let isFirst = true;

    while (true) {
      if (sessionRef.current !== session) return; // stale session — abort

      const qs = new URLSearchParams({
        ...params,
        limit: FETCH_PAGE_SIZE,
        offset,
        paginated: 'true',
      }).toString();

      try {
        const res = await fetch(`${API}/businesses?${qs}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const page = await res.json();

        if (sessionRef.current !== session) return;

        const items = page.items || [];
        const pageTotal = page.total ?? null;
        const nextOffset = page.next_offset;

        setAllBusinesses(prev => isFirst ? items : [...prev, ...items]);
        setTotal(pageTotal);
        setLoadedCount(prev => isFirst ? items.length : prev + items.length);

        if (isFirst) {
          setLoadingFirst(false);
          isFirst = false;
        }

        if (nextOffset === null || nextOffset === undefined || items.length === 0) {
          setLoadingMore(false);
          break;
        }

        // More pages to fetch
        setLoadingMore(true);
        offset = nextOffset;

        // Small yield to allow React to render the new items
        await new Promise(r => setTimeout(r, 50));
      } catch (err) {
        if (sessionRef.current !== session) return;
        console.error('[Directory] paginated fetch error:', err);
        // Only surface an error banner when the FIRST page failed (nothing to
        // show). A mid-stream failure just stops loading more — the already
        // rendered cards stay valid.
        if (isFirst) setError(true);
        setLoadingFirst(false);
        setLoadingMore(false);
        break;
      }
    }
  }, []);

  const resetAndFetch = useCallback((params = {}) => {
    paramsRef.current = params;
    const session = ++sessionRef.current;
    doFetch(params, session);
  }, [doFetch]);

  // Initial fetch on mount. On unmount, bump the session so any in-flight
  // page fetch aborts before touching state (avoids "setState on unmounted
  // component" warnings during fast navigation).
  useEffect(() => {
    const session = ++sessionRef.current;
    doFetch(paramsRef.current, session);
    return () => {
      sessionRef.current += 1;
    };
    // eslint-disable-next-line
  }, []);

  return { allBusinesses, loadingFirst, loadingMore, total, loadedCount, error, resetAndFetch, setAllBusinesses };
}

// --------------------------------------------------------------------------

const Directory = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive active view from URL param, default to orangelabel
  const activeView = searchParams.get('view') === VIEW_BTCMAP ? VIEW_BTCMAP : VIEW_ORANGELABEL;

  const {
    allBusinesses,
    loadingFirst,
    loadingMore,
    total: serverTotal,
    loadedCount,
    error: fetchError,
    resetAndFetch,
    setAllBusinesses,
  } = usePaginatedBusinesses({ approved: 'true' });

  const loading = loadingFirst;

  // Seed from the ?search= deep-link param (global search results, favorites,
  // "view existing record") so those links actually surface the chosen record
  // instead of dropping the user on the unfiltered directory.
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  // Debounced mirror of searchQuery — the filter/sort over the full pool only
  // re-runs ~200ms after the user stops typing, not on every keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 200);
    return () => clearTimeout(t);
  }, [searchQuery]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [pendingCategories, setPendingCategories] = useState([]);
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  // categoriesData from JSON is used directly — no dynamic category state needed
  const [userLocation, setUserLocation] = useState(null);
  const [radius, setRadius] = useState(null);

  // ATM toggle — off by default (ATMs are excluded unless user opts in)
  const [includeAtms, setIncludeAtms] = useState(false);

  // Sub-category filter per view (applied within the view's subset)
  const [subCategory, setSubCategory] = useState('All');

  // Global region/country/state filters
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [stateSearch, setStateSearch] = useState('');

  // Track whether user has manually changed the country filter
  const countryManuallySet = useRef(false);

  // Geo-infer the visitor's country and apply as default
  const { iso2: geoIso2, ready: geoReady } = useInferredCountry();

  // Apply geo-inferred country as default once inference is ready
  // Auto-set REGION from geo-inferred country (not country itself) on the
  // /directory root — keeps the page relevant to the visitor's region while
  // avoiding the AU-is-the-default bias that hid global-scope records when
  // a visitor in AU clicked Africa.
  useEffect(() => {
    if (!geoReady || !geoIso2) return;
    if (countryManuallySet.current) return;
    const upper = geoIso2.toUpperCase();
    const regionSlug = Object.entries(regionsData).find(([, r]) => r.countries?.includes(upper))?.[0];
    if (regionSlug) setSelectedRegion(regionSlug);
  }, [geoReady, geoIso2]);

  // Re-fetch whenever region OR country changes so the paginated fetch
  // actually hits the matching scope on the backend. Without this, a region
  // change just client-filters the previously-fetched (potentially wrong)
  // slice — e.g. selecting Africa after AU was auto-selected returned zero
  // results because the pool was fetched with ?country_code=AU.
  // Seed with the initial `${selectedRegion}|${selectedCountry}` key ('|') so
  // this effect no-ops on mount — the hook's own mount fetch already ran. Left
  // at '' it would never match the initial '|' key and fire a second, redundant
  // fetch on first render.
  const prevFetchKeyRef = useRef(`${selectedRegion}|${selectedCountry}`);
  useEffect(() => {
    const key = `${selectedRegion}|${selectedCountry}`;
    if (prevFetchKeyRef.current === key) return;
    prevFetchKeyRef.current = key;
    const params = { approved: 'true' };
    if (selectedCountry) {
      params.country_code = selectedCountry;
    } else if (selectedRegion) {
      params.region_slug = selectedRegion;
    }
    resetAndFetch(params);
  // eslint-disable-next-line
  }, [selectedRegion, selectedCountry]);

  // Derived: countries available in selected region
  const countryOptions = useMemo(() => {
    if (!selectedRegion) {
      const allCodes = Object.values(regionsData).flatMap(r => r.countries);
      return [...new Set(allCodes)]
        .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
        .filter(c => c.name)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    const regionEntry = regionsData[selectedRegion];
    if (!regionEntry) return [];
    return regionEntry.countries
      .map(code => ({ code, name: COUNTRY_NAMES[code] || code }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedRegion]);

  // Nearby search bypasses cache (location-specific result set).
  const fetchNearby = useCallback((lat = null, lng = null, rad = null) => {
    const params = { approved: 'true' };
    if (lat && lng) {
      params.lat = lat;
      params.lng = lng;
      if (rad) params.radius = rad;
    }
    resetAndFetch(params);
  }, [resetAndFetch]);

  const handleLocationChange = (location) => {
    setUserLocation(location);
    if (location) {
      fetchNearby(location.lat, location.lng, radius);
    } else {
      fetchNearby();
    }
  };

  const handleRadiusChange = (newRadius) => {
    setRadius(newRadius);
    if (userLocation) {
      fetchNearby(userLocation.lat, userLocation.lng, newRadius);
    }
  };

  // Re-run the current scope's fetch after a failure (error banner retry).
  const retryFetch = useCallback(() => {
    if (userLocation) {
      fetchNearby(userLocation.lat, userLocation.lng, radius);
      return;
    }
    const params = { approved: 'true' };
    if (selectedCountry) {
      params.country_code = selectedCountry;
    } else if (selectedRegion) {
      params.region_slug = selectedRegion;
    }
    resetAndFetch(params);
  }, [userLocation, radius, selectedCountry, selectedRegion, fetchNearby, resetAndFetch]);


  // Switch view — update URL param, reset sub-category
  const handleViewSelect = (view) => {
    setSubCategory('All');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', view);
      return next;
    });
  };

  // When region changes, reset country
  const handleRegionChange = (regionKey) => {
    setSelectedRegion(regionKey);
    setSelectedCountry('');
  };

  // When country changes, reset state search and mark as manually set
  const handleCountryChange = (countryCode) => {
    countryManuallySet.current = true;
    setSelectedCountry(countryCode);
    setStateSearch('');
  };

  // Split all businesses into the two view pools (before other filters)
  const orangelabelPool = useMemo(() => allBusinesses.filter(b => !b.btc_map), [allBusinesses]);
  const btcmapPool = useMemo(() => allBusinesses.filter(b => b.btc_map), [allBusinesses]);

  // Sub-categories derived from each pool
  const orangelabelSubCategories = useMemo(() => {
    const cats = new Set();
    orangelabelPool.forEach(b => (b.category || []).forEach(c => cats.add(c)));
    return [...cats].sort();
  }, [orangelabelPool]);

  const btcmapSubCategories = useMemo(() => {
    const cats = new Set();
    btcmapPool.forEach(b => (b.category || []).forEach(c => cats.add(c)));
    return [...cats].sort();
  }, [btcmapPool]);

  // Active view pool
  const activePool = activeView === VIEW_BTCMAP ? btcmapPool : orangelabelPool;

  // isGlobalScope lives in utils/businessScope.js and stays mirror-symmetric
  // with backend/routes/businesses.py :: global_or (shared with both map
  // pages). Any global signal must pass every region/country filter —
  // otherwise a user in e.g. Africa won't see global services like Bitrefill.

  const applyCommonFilters = useCallback((pool) => {
    let f = [...pool];

    if (selectedRegion) {
      const regionCountries = regionsData[selectedRegion]?.countries || [];
      f = f.filter(b => {
        if (isGlobalScope(b)) return true;
        const cc = (b.country_code || '').toUpperCase();
        return cc && regionCountries.includes(cc);
      });
    }
    if (selectedCountry) {
      const cc = selectedCountry.toUpperCase();
      f = f.filter(b => {
        if (isGlobalScope(b)) return true;
        return (b.country_code || '').toUpperCase() === cc;
      });
    }
    if (stateSearch.trim()) {
      const sq = stateSearch.trim().toLowerCase();
      f = f.filter(b =>
        (b.based_state || '').toLowerCase().includes(sq) ||
        (b.addr_state || '').toLowerCase().includes(sq) ||
        (b.region || '').toLowerCase().includes(sq)
      );
    }
    if (debouncedQuery) {
      const q = debouncedQuery.toLowerCase();
      f = f.filter(b =>
        (b.business_name || '').toLowerCase().includes(q) ||
        (b.category || []).some(c => c.toLowerCase().includes(q)) ||
        (b.region || '').toLowerCase().includes(q) ||
        (b.based_state || '').toLowerCase().includes(q)
      );
    }
    if (selectedCategories.length > 0) {
      f = f.filter(b => selectedCategories.some(cat => matchesCategory(b, cat)));
    }
    if (subCategory !== 'All') {
      f = f.filter(b => (b.category || []).includes(subCategory));
    }
    return f;
  }, [selectedRegion, selectedCountry, stateSearch, debouncedQuery, selectedCategories, subCategory]);

  const filteredBusinesses = useMemo(() => {
    let result = applyCommonFilters(activePool);

    // Exclude ATMs unless toggle is on
    if (!includeAtms) {
      result = result.filter(b => !isATM(b));
    }

    if (userLocation) {
      return result.sort((a, b) => {
        const aHasDistance = a.distance_km !== null && a.distance_km !== undefined;
        const bHasDistance = b.distance_km !== null && b.distance_km !== undefined;
        if (aHasDistance && !bHasDistance) return -1;
        if (!aHasDistance && bHasDistance) return 1;
        if (aHasDistance && bHasDistance) return a.distance_km - b.distance_km;
        return (a.business_name || '').localeCompare(b.business_name || '');
      });
    }
    return result.sort((a, b) => (a.business_name || '').localeCompare(b.business_name || ''));
  }, [activePool, applyCommonFilters, userLocation, includeAtms]);

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategories([]);
    setPendingCategories([]);
    setSelectedRegion('');
    setSelectedCountry('');
    setStateSearch('');
    setSubCategory('All');
    setIncludeAtms(false);
    countryManuallySet.current = false;
    // Reset view to default
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('view', VIEW_ORANGELABEL);
      return next;
    });
  };

  const hasActiveFilters = searchQuery || selectedCategories.length > 0 || selectedRegion || selectedCountry || stateSearch || subCategory !== 'All' || activeView !== VIEW_ORANGELABEL || includeAtms;

  // Active filter chips
  const activeChips = [];
  if (selectedRegion) {
    const regionName = regionsData[selectedRegion]?.name || selectedRegion;
    activeChips.push({ key: 'region', label: regionName, onRemove: () => { setSelectedRegion(''); setSelectedCountry(''); setStateSearch(''); } });
  }
  if (selectedCountry) {
    const countryName = COUNTRY_NAMES[selectedCountry] || selectedCountry;
    activeChips.push({ key: 'country', label: countryName, onRemove: () => { setSelectedCountry(''); setStateSearch(''); } });
  }
  if (stateSearch.trim()) {
    activeChips.push({ key: 'state', label: `State: ${stateSearch}`, onRemove: () => setStateSearch('') });
  }
  selectedCategories.forEach(cat => {
    activeChips.push({ key: `cat-${cat}`, label: cat, onRemove: () => setSelectedCategories(prev => prev.filter(c => c !== cat)) });
  });
  if (subCategory !== 'All') {
    activeChips.push({ key: 'subcat', label: `Service: ${subCategory}`, onRemove: () => setSubCategory('All') });
  }
  if (searchQuery) {
    activeChips.push({ key: 'search', label: `"${searchQuery}"`, onRemove: () => setSearchQuery('') });
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
            Business Directory
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Browse all Bitcoin-accepting businesses worldwide. <Link to="/map" className="text-primary hover:underline">View on map →</Link>
          </p>
        </div>

        {/* Primary view toggle cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="view-toggle-cards">
          <ViewToggleCard
            view={VIEW_ORANGELABEL}
            activeView={activeView}
            onSelect={handleViewSelect}
            title="Online, Trades & Services"
            description="OrangeLabel-listed businesses accepting Bitcoin"
            count={orangelabelPool.length}
            loading={loading}
          />
          <ViewToggleCard
            view={VIEW_BTCMAP}
            activeView={activeView}
            onSelect={handleViewSelect}
            title="BTCMap"
            description="Physical locations synced from BTCMap worldwide"
            count={btcmapPool.length}
            loading={loading}
          />
        </div>

        {/* Filters */}
        <div className="bg-card rounded-xl border-2 border-border p-3 sm:p-4 mb-4 sm:mb-6 shadow-sm space-y-3">
          {/* Row 1: Search + Near Me (desktop inline at end of search row, full available width) */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-stretch md:gap-3 gap-3">
              {/* Search */}
              <div className="relative w-full md:flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search businesses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                    <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>

              {/* Near Me — desktop only inline at end of search row, fills available width */}
              <div className="hidden md:flex md:flex-1 md:min-w-0">
                <NearbyButton
                  onLocationChange={handleLocationChange}
                  onRadiusChange={handleRadiusChange}
                  className="w-full justify-center"
                />
              </div>
            </div>

            {/* Unified filter row — Near Me (mobile) + Region + Country
                + State + Categories + ATM toggle, wraps on narrow viewports */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="md:hidden">
                <NearbyButton
                  onLocationChange={handleLocationChange}
                  onRadiusChange={handleRadiusChange}
                />
              </div>

              <select
                value={selectedRegion}
                onChange={(e) => handleRegionChange(e.target.value)}
                className="px-3 py-2 border-2 border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[130px]"
                aria-label="Filter by region"
              >
                <option value="">All Regions</option>
                {REGION_LIST.map(r => (
                  <option key={r.key} value={r.key}>{r.name}</option>
                ))}
              </select>

              <select
                value={selectedCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="px-3 py-2 border-2 border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[150px]"
                aria-label="Filter by country"
              >
                <option value="">All Countries</option>
                {countryOptions.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="State/Region..."
                  value={stateSearch}
                  onChange={(e) => setStateSearch(e.target.value)}
                  className="pl-3 pr-8 py-2 border-2 border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 w-36"
                  aria-label="Filter by state or region"
                />
                {stateSearch && (
                  <button onClick={() => setStateSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                    <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>

              <button
                data-testid="categories-btn"
                onClick={() => {
                  setPendingCategories(selectedCategories);
                  setCategoriesModalOpen(true);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                  selectedCategories.length > 0
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/40'
                }`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Categories
                {selectedCategories.length > 0 && (
                  <span className="ml-0.5 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 leading-none">
                    {selectedCategories.length}
                  </span>
                )}
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </button>

              <button
                data-testid="atm-toggle"
                onClick={() => setIncludeAtms(prev => !prev)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors select-none ${
                  includeAtms
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/40'
                }`}
                aria-pressed={includeAtms}
              >
                <span className={`w-8 h-4 rounded-full relative inline-block transition-colors duration-200 ${includeAtms ? 'bg-primary' : 'bg-muted'}`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200 ${includeAtms ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </span>
                Include ATMs
              </button>

              {hasActiveFilters && (
                <button onClick={resetFilters} className="text-xs text-primary hover:underline whitespace-nowrap">
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Categories modal */}
        <Dialog open={categoriesModalOpen} onOpenChange={setCategoriesModalOpen}>
          <DialogContent className="max-w-sm sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Filter by Category</DialogTitle>
            </DialogHeader>
            <div className="max-h-72 overflow-y-auto space-y-1 py-1" data-testid="categories-checklist">
              {categoriesData.map(cat => {
                const checked = pendingCategories.includes(cat.name);
                return (
                  <label
                    key={cat.slug}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer select-none"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        setPendingCategories(prev =>
                          checked ? prev.filter(c => c !== cat.name) : [...prev, cat.name]
                        );
                      }}
                      className="w-4 h-4 accent-primary rounded"
                    />
                    <span className="text-sm text-foreground">{cat.name}</span>
                  </label>
                );
              })}
            </div>
            <DialogFooter className="gap-2 flex-row sm:flex-row justify-between">
              <button
                onClick={() => setPendingCategories([])}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  setSelectedCategories(pendingCategories);
                  setCategoriesModalOpen(false);
                }}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Apply{pendingCategories.length > 0 ? ` (${pendingCategories.length})` : ''}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Result count + active filter chips */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {loading ? 'Loading…' : (
              <>
                {filteredBusinesses.length.toLocaleString()} {filteredBusinesses.length === 1 ? 'business' : 'businesses'}
                {loadingMore && serverTotal && loadedCount < serverTotal && (
                  <span className="ml-1 opacity-60">(loading more…)</span>
                )}
              </>
            )}
          </span>
          {activeChips.map(chip => (
            <FilterChip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
          ))}
        </div>

        {/* Business Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(8)].map((_, i) => (
              <BusinessCardSkeleton key={i} />
            ))}
          </div>
        ) : fetchError ? (
          <div className="bg-card rounded-xl border border-destructive/40 p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
                <X className="w-8 h-8 text-destructive" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">Couldn&apos;t load listings</h3>
              <p className="text-muted-foreground mb-4">Something went wrong fetching the directory. This is a connection issue, not an empty result.</p>
              <button onClick={retryFetch} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                Retry
              </button>
            </div>
          </div>
        ) : filteredBusinesses.length === 0 && !loadingMore ? (
          <div className="bg-card rounded-xl border border-border p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Search className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No businesses found</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your search or filters to find what you&apos;re looking for.</p>
              {hasActiveFilters && (
                <button onClick={resetFilters} className="text-primary hover:underline font-medium">
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {(() => {
              // When a specific country is selected, group country-bound
              // listings first and push global / service-anywhere listings
              // beneath a divider. Anything else stays flat.
              const groupByCountry = !!selectedCountry;
              if (!groupByCountry) {
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredBusinesses.map((b) => (
                      <BusinessCard key={b.id} business={b} />
                    ))}
                    {loadingMore && [...Array(3)].map((_, i) => (
                      <BusinessCardSkeleton key={`more-skel-${i}`} />
                    ))}
                  </div>
                );
              }
              const inCountry = filteredBusinesses.filter(
                (b) => (b.country_code || '').toUpperCase() === selectedCountry.toUpperCase()
              );
              const anywhere = filteredBusinesses.filter(
                (b) => (b.country_code || '').toUpperCase() !== selectedCountry.toUpperCase()
              );
              const countryLabel = COUNTRY_NAMES[selectedCountry] || selectedCountry;
              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {inCountry.map((b) => (
                      <BusinessCard key={b.id} business={b} />
                    ))}
                    {loadingMore && inCountry.length > 0 && [...Array(3)].map((_, i) => (
                      <BusinessCardSkeleton key={`more-skel-${i}`} />
                    ))}
                  </div>
                  {anywhere.length > 0 && (
                    <>
                      <div className="mt-10 mb-4 flex items-center gap-3">
                        <div className="flex-1 h-px bg-border" />
                        <h3 className="text-sm font-medium text-muted-foreground whitespace-nowrap">
                          Also available from anywhere
                          <span className="ml-2 text-xs opacity-60">
                            ({anywhere.length.toLocaleString()})
                          </span>
                        </h3>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                      <p className="mb-4 text-xs text-muted-foreground text-center">
                        Global or multi-country services — ship to {countryLabel} without being country-specific.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {anywhere.map((b) => (
                          <BusinessCard key={b.id} business={b} />
                        ))}
                      </div>
                    </>
                  )}
                </>
              );
            })()}
            {/* Progress indicator while streaming subsequent pages */}
            {loadingMore && serverTotal && (
              <div className="mt-4 text-center text-xs text-muted-foreground">
                Loading {loadedCount.toLocaleString()}/{serverTotal.toLocaleString()} more merchants…
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Directory;
